import { BehaviorSubject, Observable } from 'rxjs';
import { InjectionToken, ElementRef, Injectable } from '@angular/core';
import { PgSubscriptMessagesComponent } from './pg-subscript-messages.component';
import { SubscriptPushOption } from './pg-subscript-push-option';

import {
  animateSubscriptHostHeight,
  animateHideSubscriptMessage,
  animateShowSubscriptMessage,
} from './pg-subscript.animations';
import { PgSubscriptStateCalculator } from './pg-subscript-state-calculator';
import { ValidationErrors } from '@angular/forms';

export interface PgSubscriptController {
  checkSubscriptState(host: PgSubscriptMessagesComponent, animate: boolean);
  shownSubscriptMessage$;
}

@Injectable()
export class DefaultSubscriptController implements PgSubscriptController {
  readonly messageLineHeight = 24;

  private _shownSubscriptMessage: BehaviorSubject<{
    value: string;
    type: 'hint' | 'error';
    errorCode?: ValidationErrors;
  }> = new BehaviorSubject(null);
  public shownSubscriptMessage$: Observable<{
    value: string;
    type: 'hint' | 'error';
    errorCode?: ValidationErrors;
  }> = this._shownSubscriptMessage.asObservable();

  constructor(public pgSubscriptStateCalculator: PgSubscriptStateCalculator) {}
  checkSubscriptState(
    host: PgSubscriptMessagesComponent,
    animate: boolean = true
  ) {
    let finalPromise = <any>Promise.resolve();
    // If there is no previous iteration of the state or
    // we want to skip the animations
    if (!host.previousSubscriptDisplayState || !animate) {
      host.previousSubscriptDisplayState = this.pgSubscriptStateCalculator.calculateSubscriptState(
        host
      );
      host.previousSubscriptDisplayState.forEach(state =>
        this._setSubscriptStyleToState(host, state)
      );

      const targetSubscriptWrapperHeight = this._getTargetSubscriptWrapperHeight(
        host.previousSubscriptDisplayState,
        this._shouldSkipFirstLineHeightCalculation(host),
        this.messageLineHeight
      );
      host.renderer.setStyle(
        host.hostElement,
        'height',
        `${targetSubscriptWrapperHeight}px`
      );
    } else {
      this.animateMessageState(host, finalPromise);
    }
    return finalPromise;
  }

  private animateMessageState(
    host: PgSubscriptMessagesComponent,
    finalPromise: Promise<any>
  ) {
    const oldStates = host.previousSubscriptDisplayState;
    const newStates = this.pgSubscriptStateCalculator.calculateSubscriptState(
      host
    );

    host.previousSubscriptDisplayState = newStates;

    // calculate how tall the host subscript element
    // needs to animate to to acoomodate all of the messages

    // Temporarily this is a const variable, but I think we can make it
    // calculate based on how big the font is

    // Because we keep 24px of space initially
    // We use this flag to make sure we account for that in the
    // calculation of the height
    let firstLineCalculated = this._shouldSkipFirstLineHeightCalculation(host);

    const currentSubscriptWrapperHeight = host.hostElement.clientHeight;

    const targetSubscriptWrapperHeight = this._getTargetSubscriptWrapperHeight(
      newStates,
      firstLineCalculated,
      this.messageLineHeight
    );

    if (oldStates && oldStates.length !== newStates.length) {
      host.previousSubscriptDisplayState = newStates;
      host.previousSubscriptDisplayState.forEach(state =>
        this._setSubscriptStyleToState(host, state)
      );
      host.hostElement.style.height = `${targetSubscriptWrapperHeight}px`;
      return;
    }
    // Creating the animations for the difference in the state.
    const leavingAnimations: Function[] = [];
    const enteringAnimations: Function[] = [];

    for (let i = 0; i < newStates.length; i++) {
      const oldState = oldStates ? oldStates[i] : null;
      const newState = newStates[i];

      // The case in which a message is leaving
      if (
        (oldState && oldState.isVisible && !newState.isVisible) ||
        (!oldState && !newState.isVisible)
      ) {
        leavingAnimations.push(() =>
          animateHideSubscriptMessage(
            host.animation,
            host.renderer,
            newState.elementRef.nativeElement
          )
        );
        // The case in which a message is entering.
      } else if (
        (oldState && !oldState.isVisible && newState.isVisible) ||
        (!oldState && newState.isVisible)
      ) {
        if (host.control.stateChanges) {
          let messageType: 'error' | 'hint' = host.control.isError
            ? 'error'
            : 'hint';
          this._shownSubscriptMessage.next({
            value: newState.elementRef.nativeElement.innerHTML,
            type: messageType,
            errorCode:
              messageType === 'error' ? host.control.ngControl.errors : null,
          });
          host.messageChanges.emit(this._shownSubscriptMessage.value);
        }

        enteringAnimations.push(() =>
          animateShowSubscriptMessage(
            host.animation,
            host.renderer,
            newState.elementRef.nativeElement
          )
        );
      }
    }

    // If the animations are empty we will just exit.
    // This will happen in the case where this method is called
    // and there were no changes to the display state of the message
    if (!leavingAnimations.length && !enteringAnimations.length) {
      return finalPromise;
    }

    // If the wrapper is going to get shorter, animate it with the leaving
    // element
    if (currentSubscriptWrapperHeight > targetSubscriptWrapperHeight) {
      leavingAnimations.push(() =>
        animateSubscriptHostHeight(
          host.animation,
          host.renderer,
          host.hostElement,
          targetSubscriptWrapperHeight
        )
      );
      // Otherwise animate it with the entering element.
    } else if (currentSubscriptWrapperHeight < targetSubscriptWrapperHeight) {
      enteringAnimations.push(() =>
        animateSubscriptHostHeight(
          host.animation,
          host.renderer,
          host.hostElement,
          targetSubscriptWrapperHeight
        )
      );
    }

    // Uses the _running promise flag to
    // chain state changes that happen really quickly
    // this prevents choppy interrupted animations
    finalPromise = Promise.resolve()
      .then(() => Promise.all(leavingAnimations.map(a => a())))
      .then(() => Promise.all(enteringAnimations.map(b => b())));
  }

  private _setSubscriptStyleToState(
    host: PgSubscriptMessagesComponent,
    subscriptState: {
      isVisible: boolean;
      elementRef: ElementRef;
    }
  ) {
    const el = subscriptState.elementRef.nativeElement;
    host.renderer.setStyle(
      el,
      'visibility',
      subscriptState.isVisible ? 'visible' : 'hidden'
    );
  }

  private _getTargetSubscriptWrapperHeight(
    subscriptState,
    firstLineCalculated,
    messageLineHeight
  ) {
    return subscriptState.reduce((accumulator, cur) => {
      if (cur.isVisible) {
        if (firstLineCalculated) {
          firstLineCalculated = false;
          return Math.max(
            messageLineHeight,
            accumulator +
              cur.elementRef.nativeElement.clientHeight -
              messageLineHeight
          );
        }
        return accumulator + cur.elementRef.nativeElement.clientHeight;
      }
      return accumulator;
    }, messageLineHeight);
  }

  private _shouldSkipFirstLineHeightCalculation(
    host: PgSubscriptMessagesComponent
  ) {
    if (host.pushOption === SubscriptPushOption.ALWAYS) {
      return false;
    } else if (
      host.pushOption === SubscriptPushOption.MULTI_LINE_RESPONSIVE &&
      !host.largeViewport
    ) {
      return false;
    } else if (host.pushOption === SubscriptPushOption.NEVER) {
      return true;
    }
    return true;
  }
}

export const PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN = new InjectionToken<
  PgSubscriptController
>('PgSubscriptStateCalculator');

export const PG_SUBSCRIPT_STATE_CONTROLLER_PROVIDER = {
  provide: PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN,
  useClass: DefaultSubscriptController,
};
